import {
  ColumnDef,
  ColumnOrderTableState,
  SortingTableState,
  VisibilityTableState,
} from '@tanstack/react-table';
import {
  filter,
  findIndex,
  map,
  mapToObj,
  pipe,
  prop,
  sortBy,
  take,
} from 'remeda';

export interface ColumnConfig {
  col: string;
  visible: boolean;
  pos: number;
  sort: null | [/* index */ number, /* desc */ boolean];
}

export const configToTableState = (
  cols: ColumnConfig[],
): ColumnOrderTableState & SortingTableState & VisibilityTableState => {
  return {
    columnOrder: pipe(cols, sortBy(prop('pos')), map(prop('col'))),
    columnVisibility: pipe(
      cols,
      mapToObj(({ col, visible }) => [col, visible]),
    ),
    sorting: pipe(
      cols,
      filter((c) => c.sort !== null),
      sortBy((c) => c.sort![0]),
      take(3),
      map(({ col, sort }) => ({ id: col, desc: sort![1] })),
    ),
  };
};

export const tableStateToConfig = (
  state: ColumnOrderTableState & SortingTableState & VisibilityTableState,
): ColumnConfig[] => {
  // Guard against undefined or empty columnOrder
  if (!state.columnOrder || state.columnOrder.length === 0) {
    return [];
  }
  const newLocal = pipe(
    state.columnOrder,
    map((col) => ({
      col,
      visible: state.columnVisibility[col],
      pos: state.columnOrder.indexOf(col),
      sort: pipe(
        state.sorting,
        findIndex((s) => s.id === col),
        (idx) =>
          idx !== -1
            ? ([idx, state.sorting[idx].desc] as [number, boolean])
            : null,
      ),
    })),
  );
  return newLocal;
};

export function completeConfig(
  config: ColumnConfig[],
  columns: ColumnDef<any, any>[],
): ColumnConfig[] {
  const completeConfig = [...config];
  let idx = config.length;
  columns.forEach((c) => {
    const id = c.id || (c as any).accessorKey;
    if (!id) {
      throw new Error('Column id or accessorKey is required');
    }
    const canSort = columns.find((c) => c.id === id)?.enableSorting ?? true;
    const colCnf = completeConfig.find((cc) => cc.col === id);
    if (colCnf) {
      colCnf.sort = canSort ? colCnf.sort || [idx, true] : null;
    } else {
      idx++;
      completeConfig.push({
        col: id,
        visible: false,
        pos: idx,
        sort: canSort ? [idx, true] : null,
      });
    }
  });

  return completeConfig;
}
